import { useEffect, useState, useCallback } from 'react';
import { Player } from './Player';
import { QuickstartTheme } from '../../features/quickstart/types/theme';
import { PlayNowOverlay } from './quickstart/PlayNowOverlay';
import { QuickstartPlayerDebugOverlay } from './quickstart/QuickstartPlayerDebugOverlay';
import { MobileGamepadInputStandalone, MobileGamepadUiStandalone, MobileGamepadInputEvent } from '@playcastdotio/MobileGamepad';
import { useIsMobileDevice } from '../../utility/utility';
import { useShouldShowMobileGamepad } from '@playcastdotio/SharedComponents';
import { gamepadState, mediaStreamState } from '../../api/stores/playerStores';
import { setGamepadState } from '../../api/modules/hostMessaging/inputEvents';
import type { QuickstartController } from '../../features/quickstart/QuickstartController';
import type { PlaycastMessage, PlaycastMessageTarget } from '@playcastdotio/Messaging';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';

interface PlayerWithQuickstartProps {
  quickstartMode: boolean;
  afkTimerActive: boolean;
  adTimerActive: boolean;
  afkTimeoutSeconds: number;
  adTimerSeconds: number;
  shouldShowPlayNowOverlay?: boolean;
  theme: QuickstartTheme;
  access?: {
    mouse?: boolean;
    keyboard?: boolean;
    touch?: boolean;
    gamepad?: boolean;
  };
  onFirstInput: () => void;
  requestBrowserLocks?: () => Promise<void>;
  onWishlistClicked: () => void;
  onAfkTimeout: () => void;
  onAdTimerExpired: () => void;
  onDisconnect: () => void;
  isDebugMode?: boolean;
  controller?: QuickstartController | null;
  getAFKRemainingTime: () => number;
  getAdRemainingTime: () => number;
}

/**
 * Player wrapped with Quickstart-specific UI and behavior
 */
export const PlayerWithQuickstart = ({
  quickstartMode,
  afkTimerActive,
  adTimerActive,
  afkTimeoutSeconds,
  adTimerSeconds,
  shouldShowPlayNowOverlay: propsShowPlayNowOverlay,
  theme,
  access: propsAccess,
  onFirstInput,
  requestBrowserLocks,
  onWishlistClicked,
  onAfkTimeout,
  onAdTimerExpired,
  onDisconnect,
  isDebugMode = false,
  controller = null,
  getAFKRemainingTime,
  getAdRemainingTime,
}: PlayerWithQuickstartProps) => {
  const [showPlayNowOverlay, setShowPlayNowOverlay] = useState(true);

  // Timer remaining times - pulled from backend timer manager (synced)
  // These are purely for UI display, not for triggering callbacks
  const [afkRemainingTime, setAfkRemainingTime] = useState(afkTimeoutSeconds);
  const [playtimeRemainingTime, setPlaytimeRemainingTime] = useState(adTimerSeconds);

  // Mobile gamepad state
  const [gamepadSlot, setGamepadSlot] = useState<number>(-1);
  const [isPhysicalGamepadConnected, setIsPhysicalGamepadConnected] = useState(false);

  // Reactive mobile detection - updates when viewport changes (e.g., "Request Desktop Site")
  const isMobileDevice = useIsMobileDevice();
  // Touch-based detection for mobile gamepad - detects actual touch input vs mouse clicks
  // This handles iPads that show desktop UI but still need touch controls
  const showMobileGamepadByTouch = useShouldShowMobileGamepad();

  // Get access rights from props (passed from controller state)
  const access = propsAccess ?? {
    mouse: false,
    keyboard: false,
    touch: false,
    gamepad: false,
  };

  // Debug mode states
  const [debugShowMobileGamepad, setDebugShowMobileGamepad] = useState(false);

  // Track fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Handle mobile gamepad input and forward to peer
  const handleMobileGamepadInput = useCallback((event: MobileGamepadInputEvent) => {
    // Forward mobile gamepad state to peer connection
    // This replaces the need for gamepadStore subscription in PlayerContext
    if (event.gamepadState) {
      setGamepadState(event.gamepadState);
    }
  }, []);

  // Handle debug overlay mount - register message and controller input callbacks
  const handleDebugOverlayMount = useCallback(
    (
      messageCallback: (message: PlaycastMessage<PlaycastMessageTarget>) => void,
      controllerInputCallback: (inputType: string, eventType: string) => void,
    ) => {
      if (controller) {
        controller.setDebugMessageCallback(messageCallback);
        controller.setDebugControllerInputCallback(controllerInputCallback);
      }
    },
    [controller],
  );

  // Check for physical gamepad
  useEffect(() => {
    const checkGamepad = () => {
      const gamepads = navigator.getGamepads?.() || [];
      const hasPhysical = Array.from(gamepads).some((gp) => gp !== null);
      setIsPhysicalGamepadConnected(hasPhysical);
    };

    checkGamepad();
    window.addEventListener('gamepadconnected', checkGamepad);
    window.addEventListener('gamepaddisconnected', checkGamepad);

    return () => {
      window.removeEventListener('gamepadconnected', checkGamepad);
      window.removeEventListener('gamepaddisconnected', checkGamepad);
    };
  }, []);

  // Fullscreen/pointer lock/visibility handlers are now managed by QuickstartController
  // See setupBrowserLockHandlers() in QuickstartController.ts

  // Track fullscreen state for UI changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    // Check initial state
    setIsFullscreen(!!document.fullscreenElement);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Sync Play Now overlay visibility with coordinator's shouldShowPlayNowOverlay state
  // This allows the coordinator to explicitly control when the overlay is shown or hidden
  useEffect(() => {
    if (propsShowPlayNowOverlay !== undefined && propsShowPlayNowOverlay !== showPlayNowOverlay) {
      console.log(`[PlayerWithQuickstart] Coordinator changed shouldShowPlayNowOverlay: ${propsShowPlayNowOverlay}`);
      setShowPlayNowOverlay(propsShowPlayNowOverlay);
    }
  }, [propsShowPlayNowOverlay, showPlayNowOverlay]);

  // Listen to gamepad state for slot assignment
  useEffect(() => {
    // TODO: Get actual player ID from auth
    const playerId = 'quickstart-player';

    const listenerId = gamepadState.addListener((state) => {
      if (state.playerMappings[playerId]?.gamepadIndexes.length > 0) {
        setGamepadSlot(state.playerMappings[playerId].gamepadIndexes[0]);
      } else {
        setGamepadSlot(-1);
      }
    });

    return () => {
      gamepadState.removeListener(listenerId);
    };
  }, []);

  // Reset timer display when props change
  useEffect(() => {
    setAfkRemainingTime(afkTimeoutSeconds);
  }, [afkTimeoutSeconds]);

  useEffect(() => {
    setPlaytimeRemainingTime(adTimerSeconds);
  }, [adTimerSeconds]);

  // AFK Timer countdown - poll from backend timer manager (synchronized)
  // UI ONLY - does not trigger callbacks, purely for display
  useEffect(() => {
    if (!showPlayNowOverlay) return; // Only update when overlay is visible

    // Poll timer manager every 100ms for smooth countdown
    const interval = setInterval(() => {
      const remaining = getAFKRemainingTime();
      setAfkRemainingTime(remaining);
    }, 100);

    return () => clearInterval(interval);
  }, [showPlayNowOverlay, getAFKRemainingTime]);

  // Playtime Timer countdown - poll from backend timer manager (synchronized)
  // UI ONLY - does not trigger callbacks, purely for display
  useEffect(() => {
    if (!adTimerActive) return;
    if (showPlayNowOverlay) return; // Pause when overlay is shown

    // Poll timer manager every 100ms for smooth countdown
    const interval = setInterval(() => {
      const remaining = getAdRemainingTime();
      setPlaytimeRemainingTime(remaining);
    }, 100);

    return () => clearInterval(interval);
  }, [adTimerActive, showPlayNowOverlay, getAdRemainingTime]);

  // Listen for disconnect
  useEffect(() => {
    const handleDisconnect = () => {
      console.log('[PlayerWithQuickstart] Peer disconnected');
      onDisconnect();
    };

    window.addEventListener('playcast:peer:disconnected', handleDisconnect);

    return () => {
      window.removeEventListener('playcast:peer:disconnected', handleDisconnect);
    };
  }, [onDisconnect]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePlayNowStart = () => {
    console.log('[PlayerWithQuickstart] Play Now button clicked - starting playtime');
    setShowPlayNowOverlay(false);

    // Notify controller that Play Now was clicked
    // This transitions from playing_afk -> playing_active and starts ad timer
    onFirstInput();
  };

  // Show mobile gamepad controls if:
  // - Touch input detected AND Play Now clicked (normal flow) - handles iPads in desktop mode
  // - Debug mode with gamepad toggled (always show, even on Play Now - needed for desktop testing before fullscreen)
  const shouldShowMobileGamepad = (showMobileGamepadByTouch && access.gamepad && !showPlayNowOverlay) || (isDebugMode && debugShowMobileGamepad);

  // Show the mobile menu/exit strip for any touch-device user once Play Now is dismissed —
  // regardless of gamepad access. This ensures keyboard/mouse-only mobile users can always
  // exit the stream (fixes: no exit UI when gamepad access is denied).
  const shouldShowMobileMenu = (showMobileGamepadByTouch && !showPlayNowOverlay) || (isDebugMode && debugShowMobileGamepad);

  // Prevent drag-and-drop globally (similar to host-electron approach)
  useEffect(() => {
    const preventDrag = (e: DragEvent) => e.preventDefault();
    const preventContextMenu = (e: MouseEvent) => e.preventDefault();

    document.addEventListener('dragstart', preventDrag);
    document.addEventListener('contextmenu', preventContextMenu);

    return () => {
      document.removeEventListener('dragstart', preventDrag);
      document.removeEventListener('contextmenu', preventContextMenu);
    };
  }, []);

  return (
    <div
      className='relative h-screen w-screen select-none'
      style={
        {
          WebkitTouchCallout: 'none',
          touchAction: 'none', // Prevents all touch gestures (pan, zoom, scroll) - replaces need for passive: false
        } as React.CSSProperties
      }
    >
      {/* Play Now Overlay - Always shows AFK timer */}
      {quickstartMode && showPlayNowOverlay && (
        <PlayNowOverlay
          theme={theme}
          onStart={handlePlayNowStart}
          requestBrowserLocks={requestBrowserLocks}
          remainingSeconds={afkRemainingTime}
          isResuming={adTimerActive}
        />
      )}

      {/* Quickstart Overlays */}
      {quickstartMode && (
        <>
          {/* Play Time Remaining - Configurable Position (desktop only - mobile shows in gamepad Options button) */}
          {adTimerActive && !showPlayNowOverlay && !isMobileDevice && (() => {
            const position = theme.timerPosition || 'bottom';
            const positionClass = position === 'top' ? 'top-8' : 'bottom-8';

            return (
              <div className={`pointer-events-none fixed ${positionClass} left-1/2 z-50 -translate-x-1/2`}>
                <div className='relative overflow-hidden rounded-full shadow-lg' style={{ width: '140px' }}>
                  {/* Progress Bar Background - Depletes from left to right */}
                  <div
                    className='absolute inset-y-0 right-0 transition-all duration-1000 ease-linear'
                    style={{
                      backgroundColor: '#4338CA',
                      width: `${Math.max(0, Math.min(100, (playtimeRemainingTime / adTimerSeconds) * 100))}%`,
                    }}
                  />

                  {/* Dark overlay to make text readable */}
                  <div className='absolute inset-0' style={{ backgroundColor: 'rgba(49, 46, 129, 0.5)' }} />

                  {/* Timer */}
                  <div className='relative flex items-center justify-center px-6 py-3'>
                    <span className='text-2xl font-bold tabular-nums text-white'>{formatTime(playtimeRemainingTime)}</span>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Wishlist Button (bottom right on mobile, top right on desktop) */}
          {/* Only show during gameplay - hide when Play Now overlay is visible */}
          {theme.steamUrl && !showPlayNowOverlay && (() => {
            // Get configurable CTA text (defaults to 'Wishlist!')
            const ctaText = theme.inGameCtaText || 'Wishlist!';

            return (
              <div className={`fixed z-[1001] ${isMobileDevice ? 'bottom-0 right-0' : 'right-0 top-0'}`}>
                <button
                  onClick={onWishlistClicked}
                  tabIndex={-1}
                  className='pointer-events-auto flex flex-shrink-0 flex-col items-center justify-center border-[3px] border-black bg-gradient-to-b from-yellow-400 to-orange-500 shadow-2xl transition-opacity hover:opacity-90'
                  style={{
                    borderRadius: isMobileDevice ? '100px 0 0 0' : '0 0 0 100px',
                    width: isMobileDevice ? '110px' : '245px',
                    height: isMobileDevice ? '65px' : '128px',
                    padding: isMobileDevice ? '8px 0 4px 20px' : '10px 0 5px 55px',
                    gap: isMobileDevice ? '2px' : '5px',
                    touchAction: 'auto', // Override parent's touch-action: none to allow button clicks
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} size={isMobileDevice ? 'lg' : '4x'} className='text-black' />
                  <div className='text-left'>
                    <div className={`font-semibold leading-tight text-black ${isMobileDevice ? 'text-[8px]' : 'text-sm'}`}>
                      {isMobileDevice ? '' : isFullscreen ? 'Hold Esc' : 'Click'}
                    </div>
                    <div className={`font-semibold leading-tight text-black ${isMobileDevice ? 'text-[9px]' : 'text-sm'}`}>
                      {isMobileDevice ? `Tap to ${ctaText}` : `to ${ctaText}`}
                    </div>
                  </div>
                </button>
              </div>
            );
          })()}
        </>
      )}

      {/* Debug Overlay */}
      {isDebugMode && (
        <QuickstartPlayerDebugOverlay
          theme={theme}
          mobileGamepadEnabled={debugShowMobileGamepad}
          onToggleMobileGamepad={() => setDebugShowMobileGamepad(!debugShowMobileGamepad)}
          onDebugMediaStream={(videoStream, audioStream) => {
            // Inject debug streams into the existing Player via mediaStreamState
            console.log('[PlayerWithQuickstart] Injecting debug media streams:', {
              hasVideo: !!videoStream,
              hasAudio: !!audioStream,
            });
            mediaStreamState.updateState({
              stream: videoStream || new MediaStream(),
              audioStream: audioStream || new MediaStream(),
            });
          }}
          onMount={handleDebugOverlayMount}
        />
      )}

      {/* Mobile Gamepad / Menu — always show the exit menu strip on mobile once Play Now is dismissed.
          Gamepad input and controls are only shown when the user has gamepad access.
          This ensures keyboard/mouse-only mobile users can always exit the stream. */}
      {shouldShowMobileMenu && (
        <>
          {shouldShowMobileGamepad && (
            <MobileGamepadInputStandalone
              enabled={true}
              access={access}
              buttonChains={theme.mobileGamepadConfig?.buttonChains}
              onInput={handleMobileGamepadInput}
            />
          )}
          <MobileGamepadUiStandalone
            enabled={true}
            testMode={isDebugMode}
            gamepadSlot={gamepadSlot}
            isMouseEnabled={access.mouse}
            isKeyboardEnabled={access.keyboard}
            disconnect={onDisconnect}
            isPhysicalGamepadConnected={isPhysicalGamepadConnected}
            hideGamepadControls={!shouldShowMobileGamepad}
            timerRemainingSeconds={adTimerActive && !showPlayNowOverlay ? playtimeRemainingTime : undefined}
            timerTotalSeconds={adTimerActive && !showPlayNowOverlay ? adTimerSeconds : undefined}
            timerPosition={theme.timerPosition}
          />
        </>
      )}

      {/* Player Component */}
      <Player isQuickstartMode={quickstartMode} />
    </div>
  );
};

